import type { Plan } from 'js/src/client';
import { ApiService, SubscriptionFailureReasons, Urls } from 'js/src/client';
import type { PlanSwitchErrorInfo } from 'js/src/components/pricing/types';
import { ANALYTICS_QS, UPGRADE_STATUS } from 'js/src/components/pricing/types';
import {
    setChangingSubscription,
    setConfirmationModalData,
    setPlanSwitchErrorInfo,
    setRetryUpgradeInfo,
    setShowPlanSwitchErrorModal,
    setShowSubscriptionChangeConfirmation,
    setUpgradeStatus,
    setUserHasPaymentMethodSetup,
} from 'js/src/data/pricing/pricing-slice';
import { CHECKOUT_SESSION_SOURCE_PRICING } from 'js/src/libs/consts';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import PaymentService from 'js/src/libs/payment-service';
import QueryStringManipulator from 'js/src/libs/querystring-manipulator';
import type { AppDispatch } from 'js/src/libs/RootState';
import type { GetRootState } from 'js/src/luminary/luminary-redux-connect-types';
import { isApiError, isApiErrorWithProperty } from 'js/src/types';

export function switchToPlan(plan: Plan) {
    return async (dispatch: AppDispatch, getState: GetRootState) => {
        const state = getState();
        const userHasPaymentMethodSetup = state.pricing.userHasPaymentMethodSetup;
        const subscriptionDetails = state.pricing.subscriptionDetails;
        const upgradeStatus = state.pricing.upgradeStatus;
        const showAnnualPromo = Urls.SPECIAL_PRICING === window.location.pathname;

        if (SERVER.USER && SERVER.USER.plan !== '' && plan.name === '') {
            // Downgrading to free plan
            window.location.href = Urls.ACCOUNT_CANCEL;
            return;
        }

        if (plan.is_enterprise) {
            // redirect to enterprise page
            window.location.href = Urls.ENTERPRISE;
            return;
        }

        if (
            (plan.name === '' || userHasPaymentMethodSetup) &&
            subscriptionDetails?.status !== 'incomplete'
        ) {
            // Check for user confirmation via popup -> then trigger purchase.
            dispatch(
                setConfirmationModalData({
                    plan,
                    email: SERVER.USER.email,
                }),
            );
            dispatch(setShowSubscriptionChangeConfirmation(true));
        } else if (upgradeStatus === UPGRADE_STATUS.CARD_DECLINED) {
            try {
                const checkout_session_id = await dispatch(
                    startUpdatePaymentMethodCheckoutSession(
                        SERVER.USER.email,
                        plan,
                        showAnnualPromo,
                    ),
                );
                await dispatch(loadNewCheckoutSession(checkout_session_id));
            } catch (error) {
                ErrorReporter.capture(error);
            }
        } else {
            try {
                const checkout_session_id = await dispatch(
                    startNewSubscriptionCheckoutSession(SERVER.USER.email, plan, showAnnualPromo),
                );
                await dispatch(loadNewCheckoutSession(checkout_session_id));
            } catch (error) {
                ErrorReporter.capture(error);
            }
        }
    };
}

export function startNewSubscriptionCheckoutSession(
    email: string,
    plan: Plan,
    showAnnualPromo: boolean,
) {
    return async (dispatch: AppDispatch, getState: GetRootState) => {
        const redirectPath = showAnnualPromo ? Urls.SPECIAL_PRICING : Urls.PRICING;

        dispatch(prepareSubscriptionChange());

        let coupon = '';
        const state = getState();
        const couponStatus = state.pricing.couponStatus;
        if (couponStatus !== null && couponStatus.valid) {
            coupon = couponStatus.id;
        }

        try {
            const response = await ApiService.pricingSubscriptionCreate({
                body: {
                    plan_name: plan.name,
                    email: email,
                    coupon: coupon,
                    success_path: redirectPath,
                    cancel_path: redirectPath,
                },
            });
            return response.data.checkout_session_id;
        } catch (error: unknown) {
            ErrorReporter.capture(error);
            dispatch(handleSubscriptionChangeError(error, plan, email, coupon));
            throw error;
        }
    };
}

export function startUpdatePaymentMethodCheckoutSession(
    email: string,
    plan: Plan,
    showAnnualPromo: boolean,
) {
    return async (dispatch: AppDispatch, getState: GetRootState) => {
        const redirectPath = showAnnualPromo ? Urls.SPECIAL_PRICING : Urls.PRICING;

        dispatch(prepareSubscriptionChange());

        let coupon = '';
        const state = getState();
        const couponStatus = state.pricing.couponStatus;
        if (couponStatus !== null && couponStatus.valid) {
            coupon = couponStatus.id;
        }

        try {
            const response = await ApiService.pricingPaymentMethodCheckoutUpdate({
                body: {
                    selected_plan: plan.name,
                    source: CHECKOUT_SESSION_SOURCE_PRICING,
                    success_path: redirectPath,
                    cancel_path: redirectPath,
                },
            });
            return response.data.checkout_session_id;
        } catch (error: unknown) {
            ErrorReporter.capture(error);
            dispatch(handleSubscriptionChangeError(error, plan, email, coupon));
            throw error;
        }
    };
}

export function prepareSubscriptionChange() {
    return (dispatch: AppDispatch) => {
        dispatch(setShowSubscriptionChangeConfirmation(false));
        dispatch(setChangingSubscription(true));
        dispatch(setUpgradeStatus(null));
        dispatch(setRetryUpgradeInfo(null));
    };
}

export function updateSubscription(email: string, plan: Plan) {
    return async (dispatch: AppDispatch, getState: GetRootState) => {
        dispatch(prepareSubscriptionChange());

        let coupon = '';
        const state = getState();
        const couponStatus = state.pricing.couponStatus;
        if (couponStatus !== null && couponStatus.valid) {
            coupon = couponStatus.id;
        }

        try {
            const response = await ApiService.pricingSubscriptionPartialUpdate({
                body: {
                    plan_name: plan.name,
                    email: email,
                    coupon: coupon,
                },
            });

            // notify the user if we require further action from their end
            if (response.data.reason === SubscriptionFailureReasons.SCA_REQUIRED) {
                // Let's try getting the user to authenticate the charge
                if (response.data.client_secret === null) throw new Error('Client secret is null');
                await dispatch(handleSCARequired(response.data.client_secret, plan.name));
            }
            // otherwise success! Reload the page
            else {
                await dispatch(waitForPlanToUpdate(plan.name));
            }
        } catch (error: unknown) {
            ErrorReporter.capture(error);
            dispatch(handleSubscriptionChangeError(error, plan, email, coupon));
        }
    };
}

export function loadNewCheckoutSession(checkout_session_id: string) {
    return async (dispatch: AppDispatch) => {
        try {
            await PaymentService.redirectToCheckout(checkout_session_id);
        } catch (error) {
            // This happens if `redirectToCheckout` fails due to a browser or network error
            ErrorReporter.capture(error);
            throw new Error(
                'Could not load required payment service. Please try again in a different browser or internet network. Contact support@lumen5.com for additional support.',
            );
        }
    };
}

export function handleSubscriptionChangeError(
    error: unknown,
    plan: Plan,
    email: string,
    coupon: string,
) {
    return (dispatch: AppDispatch) => {
        let reason = '';
        if (isApiErrorWithProperty(error, 'reason')) {
            reason = error.body.reason as string;
            switch (reason) {
                case 'credit_card_declined': {
                    dispatch(setUpgradeStatus(UPGRADE_STATUS.CARD_DECLINED));
                    dispatch(
                        setRetryUpgradeInfo({
                            plan,
                        }),
                    );

                    break;
                }
                case 'invalid_customer': {
                    dispatch(
                        setRetryUpgradeInfo({
                            plan,
                        }),
                    );
                    // Call retrySubscriptionChange after state update
                    setTimeout(() => {
                        dispatch(retrySubscriptionChange());
                    }, 0);

                    break;
                }
                case 'team_size_error': {
                    dispatch(setUpgradeStatus(UPGRADE_STATUS.TEAM_SIZE_ERROR));

                    break;
                }
                default: {
                    dispatch(setUpgradeStatus(UPGRADE_STATUS.GENERIC_ERROR));
                }
            }
        } else if (isApiError(error) && error.status === 403) {
            dispatch(setUpgradeStatus(UPGRADE_STATUS.PERMISSION_ERROR));
        } else {
            dispatch(setUpgradeStatus(UPGRADE_STATUS.GENERIC_ERROR));
        }

        analytics.track('Error updating subscription', {
            plan_name: plan.name,
            email: email,
            coupon: coupon,
            response: error,
            reason,
        });
        dispatch(setChangingSubscription(false));
    };
}

export function handlePlanSwitchError(errorInfo: PlanSwitchErrorInfo) {
    return (dispatch: AppDispatch) => {
        dispatch(setPlanSwitchErrorInfo(errorInfo));
        dispatch(setShowPlanSwitchErrorModal(true));
    };
}

export function togglePlanSwitchErrorModal(visibilityFlag: boolean) {
    return (dispatch: AppDispatch) => {
        dispatch(setShowPlanSwitchErrorModal(visibilityFlag));
    };
}

export function handleSCARequired(client_secret: string, plan: string) {
    return async (dispatch: AppDispatch) => {
        try {
            const result = await PaymentService.confirmCardPayment(client_secret);
            if (result.paymentIntent) {
                await dispatch(waitForPlanToUpdate(plan));
            } else {
                dispatch(setUpgradeStatus(UPGRADE_STATUS.SCA_REQUIRED));
                dispatch(setChangingSubscription(false));
            }
        } catch (error) {
            ErrorReporter.capture(error);
        }
    };
}

export function waitForPlanToUpdate(selectedPlan: string) {
    return async (dispatch: AppDispatch) => {
        let retryCount = 0;
        const maxRetries = 15;

        const checkPlan = async () => {
            try {
                const response = await ApiService.pricingCurrentPlanNameRetrieve();

                if (response.data.payment_status === 'requires_action') {
                    if (response.data.client_secret === null)
                        throw new Error('Client secret is null');
                    await dispatch(handleSCARequired(response.data.client_secret, selectedPlan));
                } else {
                    const updatedPlan = response.data.plan_name;
                    if (selectedPlan !== updatedPlan && retryCount < maxRetries) {
                        retryCount++;
                        setTimeout(checkPlan, 1000);
                    } else if (selectedPlan === updatedPlan) {
                        // Success - reload the page
                        reloadPageSuccess();
                    } else {
                        dispatch(setUpgradeStatus(UPGRADE_STATUS.GENERIC_ERROR));
                        dispatch(setChangingSubscription(false));
                    }
                }
            } catch (error: unknown) {
                ErrorReporter.capture(error);
                if (retryCount < maxRetries) {
                    retryCount++;
                    setTimeout(checkPlan, 1000);
                } else {
                    dispatch(setUpgradeStatus(UPGRADE_STATUS.GENERIC_ERROR));
                    dispatch(setChangingSubscription(false));
                }
            }
        };

        await checkPlan();
    };
}

export function reloadPageSuccess() {
    // reload with success message, removing cpn param if there, and adding the old plan price
    let new_location = QueryStringManipulator.updateQueryStringParameter(
        window.location.toString(),
        'success',
        'true',
    );
    new_location = QueryStringManipulator.updateQueryStringParameter(new_location, 'cpn', '');
    new_location = QueryStringManipulator.updateQueryStringParameter(
        new_location,
        ANALYTICS_QS.KEY,
        `${SERVER.USER.plan_price}`,
    );
    window.location.href = new_location;
}

export function retrySubscriptionChange() {
    return (dispatch: AppDispatch, getState: GetRootState) => {
        /*
        Requires the retry_upgrade_info object to be set, should be called
        when a user click 'Try a different card' after a charge was declined
         */
        dispatch(setUserHasPaymentMethodSetup(false));

        const state = getState();
        const retryUpgradeInfo = state.pricing.retryUpgradeInfo;

        if (retryUpgradeInfo === null || retryUpgradeInfo.plan === undefined) {
            alert("Couldn't find the plan to retry.  Please select a plan.");
        } else {
            dispatch(switchToPlan(retryUpgradeInfo.plan));
        }
    };
}
